"use client";

import { useEffect } from "react";
import Logo from "@/app/(general-header)/header/logo";
import { useAppDispatch } from "@/app/redux/hooks";
import { setActiveCompetitions } from "@/app/redux/features/categories/actions";
import { useGetPrizeStraplineTextsQuery } from "@/app/redux/features/api/membersHome";

type Stat = {
  icon: string;
  alt: string;
  value: string | undefined;
  label: string;
};

const CustomHeaderV4 = () => {
  const { data: strapline } = useGetPrizeStraplineTextsQuery();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const activeCompetitionsPromise = dispatch(setActiveCompetitions());
    activeCompetitionsPromise
      .unwrap()
      .then(res => {
        const syncEvent = new CustomEvent("sync-header-store", {
          detail: res,
        });
        window.dispatchEvent(syncEvent);
      })
      .catch(err => console.error(err));

    return () => {
      activeCompetitionsPromise.abort();
    };
  }, []);

  const stats: Stat[] = [
    {
      icon: "/assets/images/icon-est.svg",
      alt: "Established",
      value: strapline?.PrizeStraplineText1,
      label: "Years",
    },
    {
      icon: "/assets/images/icon-winners.svg",
      alt: "Winners",
      value: strapline?.PrizeStraplineText3,
      label: "Winners",
    },
    {
      icon: "/assets/images/icon-prizes.svg",
      alt: "Prizes",
      value: strapline?.PrizeStraplineText2,
      label: "Prizes",
    },
  ];

  const renderStat = ({ icon, alt, value, label }: Stat, isMobile: boolean) =>
    isMobile ? (
      <div key={label} className="flex items-center gap-1 shrink-0">
        <img src={icon} alt={alt} className="size-[18px] shrink-0" />
        <div className="flex items-center gap-1 text-[10px] leading-none whitespace-nowrap">
          <span className="font-jost font-semibold text-dark-1">{value}</span>
          <span className="font-jost font-medium text-gray-1">{label}</span>
        </div>
      </div>
    ) : (
      <div key={label} className="flex items-center gap-2 shrink-0">
        <img src={icon} alt={alt} className="size-6 shrink-0" />
        <div className="flex flex-col items-start gap-1 whitespace-nowrap">
          <span className="font-jost font-semibold text-[13px] leading-[1.2] text-dark-1">{value}</span>
          <span className="font-jost font-medium text-[11px] leading-[14px] text-gray-1">{label}</span>
        </div>
      </div>
    );

  return (
    <div className="sticky top-0 z-[1003] w-full bg-white">
      <div className="backdrop-blur-[10px] border-b border-solid border-black/8">
        <div className="relative flex items-center justify-between h-14 lg:h-[60px] px-3 lg:px-4 lg:max-w-[1276px] mx-auto gap-2 lg:gap-4">
          <button
            type="button"
            onClick={() => {
              if (location.pathname !== "/") {
                location.href = "/#featured";
                return;
              }
              const scrollToCategoryEvent = new CustomEvent("scroll-to-category", {
                detail: "featured",
              });
              window.dispatchEvent(scrollToCategoryEvent);
            }}
            className="flex items-center gap-1 lg:gap-2 shrink-0"
            aria-label="Back to Featured Competitions">
            <span className="flex items-center justify-center size-6 lg:size-8 shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12L19 12M5 12L11 18M5 12L11 6"
                  stroke="#28282B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="font-jost font-medium lg:font-normal text-[11px] lg:text-sm leading-[14px] lg:leading-5 text-left text-gray-1">
              Featured <br className="lg:hidden" />
              Competitions
            </span>
          </button>

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <Logo />
          </div>

          <div className="hidden lg:flex items-center gap-4 shrink-0">{stats.map(stat => renderStat(stat, false))}</div>
        </div>
      </div>

      <div className="lg:hidden flex items-center justify-center h-7 gap-[13px] bg-white shadow-[0_2px_4px_0_rgba(0,0,0,0.1)]">
        {stats.map(stat => renderStat(stat, true))}
      </div>
    </div>
  );
};

export default CustomHeaderV4;
